// Sponsors admin — geen tiers meer (klant gebruikt ze niet).
// Wel: logo-upload naar storage bucket 'sponsor-logos' + zichtbaarheid +
// sorteervolgorde + edit-form per sponsor.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useRef } from "react";
import { listSponsors } from "@/lib/api";
import { useCurrentEvent } from "@/lib/event-context";
import { supabase } from "@/lib/supabase";
import type { Tables } from "@/types/database";

type Sponsor = Tables<"sponsors">;
const LOGO_BUCKET = "sponsor-logos";

function logoPublicUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  const { data } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

interface DraftSponsor {
  name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  website: string;
  internal_notes: string;
  visible: boolean;
  sort: number;
}

const EMPTY_DRAFT: DraftSponsor = {
  name: "", contact_name: "", contact_email: "", contact_phone: "",
  website: "", internal_notes: "", visible: false, sort: 0,
};

export function SponsorsIndex() {
  const { data: event } = useCurrentEvent();
  const qc = useQueryClient();
  const { data: sponsors, isLoading } = useQuery({
    queryKey: ["sponsors", event?.id],
    queryFn: () => listSponsors(event!.id),
    enabled: !!event?.id,
  });

  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [draft, setDraft] = useState<DraftSponsor>(EMPTY_DRAFT);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPathPreview, setLogoPathPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function startNew() {
    setEditing("new");
    setDraft({ ...EMPTY_DRAFT, sort: ((sponsors?.length ?? 0) + 1) * 10 });
    setLogoFile(null);
    setLogoPathPreview(null);
  }

  function startEdit(s: Sponsor) {
    setEditing(s.id);
    setDraft({
      name: s.name,
      contact_name: s.contact_name ?? "",
      contact_email: s.contact_email ?? "",
      contact_phone: s.contact_phone ?? "",
      website: s.website ?? "",
      internal_notes: s.internal_notes ?? "",
      visible: s.visible,
      sort: s.sort,
    });
    setLogoFile(null);
    setLogoPathPreview(s.logo_path);
  }

  function cancelEdit() {
    setEditing(null);
    setLogoFile(null);
    setLogoPathPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function uploadLogoIfNeeded(sponsorId: string): Promise<string | null> {
    if (!logoFile) return logoPathPreview ?? null;
    const ext = logoFile.name.split(".").pop()?.toLowerCase() ?? "png";
    const path = `${event!.id}/${sponsorId}.${ext}`;
    const { error } = await supabase.storage
      .from(LOGO_BUCKET)
      .upload(path, logoFile, { upsert: true, cacheControl: "3600", contentType: logoFile.type });
    if (error) throw new Error(`Logo upload faalde: ${error.message}`);
    return path;
  }

  async function saveDraft() {
    if (!event || !draft.name.trim()) return;
    setBusy(true);
    try {
      if (editing === "new") {
        // Insert eerst zonder logo om id te krijgen
        const { data: created, error } = await supabase.from("sponsors").insert({
          event_id: event.id,
          name: draft.name.trim(),
          contact_name: draft.contact_name || null,
          contact_email: draft.contact_email || null,
          contact_phone: draft.contact_phone || null,
          website: draft.website || null,
          internal_notes: draft.internal_notes || null,
          visible: draft.visible,
          sort: draft.sort,
        }).select("id").single();
        if (error || !created) throw error ?? new Error("insert failed");

        const logoPath = await uploadLogoIfNeeded(created.id);
        if (logoPath) {
          const { error: upErr } = await supabase
            .from("sponsors")
            .update({ logo_path: logoPath })
            .eq("id", created.id);
          if (upErr) throw upErr;
        }
        setToast("✓ Sponsor toegevoegd");
      } else if (editing) {
        const logoPath = await uploadLogoIfNeeded(editing);
        const { error } = await supabase.from("sponsors").update({
          name: draft.name.trim(),
          contact_name: draft.contact_name || null,
          contact_email: draft.contact_email || null,
          contact_phone: draft.contact_phone || null,
          website: draft.website || null,
          internal_notes: draft.internal_notes || null,
          visible: draft.visible,
          sort: draft.sort,
          logo_path: logoPath,
          updated_at: new Date().toISOString(),
        }).eq("id", editing);
        if (error) throw error;
        setToast("✓ Sponsor opgeslagen");
      }
      qc.invalidateQueries({ queryKey: ["sponsors"] });
      cancelEdit();
    } catch (err) {
      setToast(`Fout: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 3000);
    }
  }

  const toggleVisible = useMutation({
    mutationFn: async ({ id, visible }: { id: string; visible: boolean }) => {
      const { error } = await supabase.from("sponsors")
        .update({ visible, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sponsors"] }),
  });

  const deleteMut = useMutation({
    mutationFn: async (s: Sponsor) => {
      // Cleanup logo van storage indien aanwezig
      if (s.logo_path) {
        await supabase.storage.from(LOGO_BUCKET).remove([s.logo_path]);
      }
      const { error } = await supabase.from("sponsors").delete().eq("id", s.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sponsors"] }),
  });

  return (
    <>
      <div className="panel">
        <div className="panel-h">
          <h3>Sponsors ({sponsors?.length ?? 0})</h3>
          {editing === null && (
            <button className="btn-admin sm" onClick={startNew}>+ Sponsor toevoegen</button>
          )}
        </div>

        {editing !== null && (
          <div style={{ padding: "1.2rem", border: "1px solid rgba(255,122,26,0.3)", borderRadius: 6, marginBottom: "1.2rem", background: "rgba(255,122,26,0.04)" }}>
            <div style={{ fontFamily: "var(--f-mono)", fontSize: "0.7rem", letterSpacing: "0.18em", color: "var(--c-orange)", textTransform: "uppercase", marginBottom: "1rem" }}>
              {editing === "new" ? "Nieuwe sponsor" : "Sponsor bewerken"}
            </div>

            <div className="form-grid">
              <div className="field">
                <label>Bedrijfsnaam *</label>
                <input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  required
                />
              </div>
              <div className="field">
                <label>Website</label>
                <input
                  value={draft.website}
                  onChange={(e) => setDraft({ ...draft, website: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="field">
                <label>Contact naam</label>
                <input
                  value={draft.contact_name}
                  onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Contact email</label>
                <input
                  type="email"
                  value={draft.contact_email}
                  onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Telefoon</label>
                <input
                  value={draft.contact_phone}
                  onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Sorteervolgorde (lager = eerder)</label>
                <input
                  type="number"
                  value={draft.sort}
                  onChange={(e) => setDraft({ ...draft, sort: Number(e.target.value) })}
                />
              </div>
            </div>

            <div className="field">
              <label>Logo</label>
              <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                {(logoFile || logoPathPreview) && (
                  <div style={{
                    width: 96, height: 96,
                    background: "var(--c-cream)", borderRadius: 6, padding: 8,
                    border: "1px solid rgba(255,122,26,0.3)",
                    display: "grid", placeItems: "center",
                  }}>
                    <img
                      src={logoFile ? URL.createObjectURL(logoFile) : logoPublicUrl(logoPathPreview)!}
                      alt="logo preview"
                      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                    />
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <small style={{ opacity: 0.6, fontFamily: "var(--f-mono)", fontSize: "0.72rem" }}>
                PNG / SVG / JPG / WebP. Bij voorkeur transparant (PNG/SVG) en min. 400px breed.
              </small>
            </div>

            <div className="field">
              <label>Interne notities (niet zichtbaar voor publiek)</label>
              <textarea
                rows={2}
                value={draft.internal_notes}
                onChange={(e) => setDraft({ ...draft, internal_notes: e.target.value })}
              />
            </div>

            <div className="field">
              <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={draft.visible}
                  onChange={(e) => setDraft({ ...draft, visible: e.target.checked })}
                />
                Tonen op publieke website
              </label>
            </div>

            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                className="btn-admin sm"
                onClick={saveDraft}
                disabled={busy || !draft.name.trim()}
              >
                {busy ? "Bezig…" : editing === "new" ? "→ Toevoegen" : "💾 Opslaan"}
              </button>
              <button className="btn-admin ghost sm" onClick={cancelEdit} disabled={busy}>
                Annuleer
              </button>
            </div>
          </div>
        )}

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Logo</th>
                <th>Naam</th>
                <th>Contact</th>
                <th>Zichtbaar</th>
                <th>Sort</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="empty"><span className="spinner" /> Laden…</td></tr>}
              {(sponsors ?? []).map((s) => {
                const url = logoPublicUrl(s.logo_path);
                return (
                  <tr key={s.id}>
                    <td>
                      {url ? (
                        <div style={{ width: 60, height: 40, background: "var(--c-cream)", borderRadius: 4, padding: 4, display: "grid", placeItems: "center" }}>
                          <img src={url} alt={s.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                        </div>
                      ) : (
                        <span style={{ opacity: 0.4, fontFamily: "var(--f-mono)", fontSize: "0.72rem" }}>geen logo</span>
                      )}
                    </td>
                    <td>
                      <strong>{s.name}</strong>
                      {s.website && <><br /><a href={s.website} target="_blank" rel="noopener" style={{ fontSize: "0.78rem", color: "var(--c-orange)" }}>{s.website}</a></>}
                    </td>
                    <td>
                      {s.contact_name && <>{s.contact_name}<br /></>}
                      <small>{s.contact_email}</small>
                      {s.contact_phone && <><br /><small>{s.contact_phone}</small></>}
                    </td>
                    <td>
                      <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={s.visible}
                          onChange={(e) => toggleVisible.mutate({ id: s.id, visible: e.target.checked })}
                        />
                        {s.visible ? "Op site" : "Verborgen"}
                      </label>
                    </td>
                    <td><small>{s.sort}</small></td>
                    <td>
                      <div className="row-actions">
                        <button className="btn-mini" onClick={() => startEdit(s)}>Bewerk</button>
                        <button
                          onClick={() => {
                            if (confirm(`Sponsor '${s.name}' verwijderen?`)) deleteMut.mutate(s);
                          }}
                          disabled={deleteMut.isPending}
                        >Verwijder</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!isLoading && (sponsors ?? []).length === 0 && (
                <tr><td colSpan={6} className="empty">Nog geen sponsors. Klik '+ Sponsor toevoegen'.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {toast && <div className={`toast ${toast.startsWith("✓") ? "success" : "error"}`} onClick={() => setToast(null)}>{toast}</div>}
    </>
  );
}
